import React, { useEffect, useRef, useState } from 'react';
import {
  Modal,
  View,
  Text,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  TextInput,
  KeyboardAvoidingView,
  Platform,
  Image,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { avatarColorForName, getBannerDetailImage, getBannerLink } from '../../data/promoSocialDemo';
import { useCustomAlertModal } from '../CustomAlertModal';

function initials(name) {
  const parts = String(name || '?').trim().split(' ');
  return (parts[0]?.[0] || '?').toUpperCase();
}

function createdAt( currentTs, ts ) {
  const seconds = Math.floor((currentTs - ts) / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);

  if (weeks > 0) return `${weeks}w`;
  if (days > 0) return `${days}d`;
  if (hours > 0) return `${hours}h`;
  if (minutes > 0) return `${minutes}m`;
  if (seconds > 10) {
    const secondDisplay = Math.floor(seconds / 10) * 10;
    return `${secondDisplay}s`;
  } else {
    return `just now`;
  }
}

function formatCount(n) {
  if (n >= 1000) {
    return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
  }
  return (Number(n) || 0).toLocaleString('id-ID');;
}

function Avatar({ name, uri }) {
  if (uri) {
    return <Image source={{ uri }} style={styles.avatar} />;
  }
  return (
    <View style={[styles.avatar, { backgroundColor: avatarColorForName(name) }]}>
      <Text style={styles.avatarText}>{initials(name)}</Text>
    </View>
  );
}

function CommentRow({ comment, now, currentUser, isReply, onReply, onDelete, onLike }) {
  return (
    <View style={[styles.commentRow, isReply && styles.replyRow]}>
      <Avatar name={comment.user} uri={comment.avatar} />
      <View style={styles.commentBody}>
        <Text style={styles.commentText}>
          <Text style={styles.commentUser}>{comment.user} </Text>
          {comment.mention && (
            <Text style={styles.commentUser}>> {comment.mention} </Text>
          )}
          {comment.text}
        </Text>
        <View style={styles.metaRow}>
          <Text style={styles.meta}>{createdAt(now, comment.timestamp)}</Text>
          {!isReply ? (
            <TouchableOpacity onPress={() => onReply(comment)} hitSlop={6}>
              <Text style={styles.replyLink}>Balas</Text>
            </TouchableOpacity>
          ) : null}
        </View>
      </View>
      
      <View style={{alignItems: 'center'}}>
        <TouchableOpacity style={styles.commentLike} onPress={() => onLike(comment.id)} hitSlop={6}>
          <Ionicons
            name={comment.likers.includes(currentUser.id) ? 'heart' : 'heart-outline'}
            size={16}
            color={comment.likers.includes(currentUser.id) ? '#ED4956' : '#1E293B'}
          />
        </TouchableOpacity>
        {comment.likers.length > 0 && (
          <Text style={styles.commentLikeText}>{formatCount(comment.likers.length)}</Text>
        )}
      </View>
      
      {comment.userId === currentUser.id && (
        <TouchableOpacity style={styles.delete} onPress={() => onDelete(comment.id)} hitSlop={6}>
          <Ionicons name="trash" size={16} color="#FF0000" />
        </TouchableOpacity>
      )}
    </View>
  );
}

export default function PromoCommentSheet({
  visible,
  item,
  comments,
  currentUser,
  onClose,
  onSubmit,
  onDelete,
  onLike
}) {
  const scrollerRef = useRef(null);
  const [commentCoordinates, setCommentCoordinates] = useState({});
  const [commentIdHighlight, setCommentIdHighlight] = useState(null);

  const [text, setText] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const inputRef = useRef(null);
  const [currentTs, setCurrentTs] = useState(Date.now());
  const [aspectRatio, setAspectRatio] = useState(1);
  const { 
    customAlertModal, 
    showCustomAlert, 
    hideCustomAlert, 
    handleCustomAlertConfirm
  } = useCustomAlertModal();

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTs( Date.now() );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!visible) {
      setText('');
      setReplyTo(null);
    } else {
      // dynamic image size
      Image.getSize( getBannerDetailImage(item), (width, height) => {
        if(width && height) {
          setAspectRatio(width / height);
        } else {
          setAspectRatio(1);
        }
      } );
    }
  }, [visible, item?.id]);

  useEffect(() => {
    if (commentIdHighlight) {
      scrollToComment(commentIdHighlight);
      setCommentIdHighlight(null);
    }
  }, [commentIdHighlight, commentCoordinates]);

  const handleReply = (comment) => {
    setReplyTo(comment);
    setTimeout(() => inputRef.current?.focus?.(), 80);
  };

  const handleSend = async () => {
    const result = await onSubmit(item?.id, replyTo?.id || null, text);
    if (result.success) {
      setText('');
      setReplyTo(null);

      if (result.comment) {
        setCommentIdHighlight(result.comment.parentId || result.comment.id || 0);
      }

      if ((result.hidden || false)) {
        showCustomAlert(
          `Pemberitahuan`,
          `Komentarmu berhasil dikirimkan`,
          `success`
        );
      }
    }
  };

  const handleDelete = (commentId) => {
    onDelete(item.id, commentId);
  }

  const handleLike = (commentId) => {
    onLike(item.id, commentId);
  }

  const handleImagePress = () => {
    const link = getBannerLink(item);
    if(link) {
      window.open(link, '_blank');
      return;
    }
  }

  const scrollToComment = (id) => {
    const yOffset = commentCoordinates[id];
    if (yOffset !== undefined) {
      scrollerRef.current?.scrollTo({
        x: 0,
        y: Math.max(0, yOffset - 50),
        animated: true, // Smooth scrolling
      });
    }
  };

  const placeholder = replyTo ? `Balas ${replyTo.user}...` : 'Tulis komentar...';

  return (
    <Modal visible={visible} transparent animationType="slide" onRequestClose={onClose}>
      <KeyboardAvoidingView
        style={styles.viewport}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
        pointerEvents="box-none"
      >
        <TouchableOpacity style={styles.outside} activeOpacity={1} onPress={onClose} />
        <View style={styles.column} pointerEvents="box-none">
          <TouchableOpacity style={styles.backdrop} activeOpacity={1} onPress={onClose} />
          <View style={styles.sheet}>
          <View style={styles.handle} />
          <View style={styles.header}>
            <Text style={styles.headerTitle}>Komentar</Text>
            <TouchableOpacity onPress={onClose} hitSlop={10} style={styles.closeBtn}>
              <Ionicons name="close" size={20} color="#0F172A" />
            </TouchableOpacity>
          </View>

          <ScrollView
            ref={scrollerRef}
            style={styles.list}
            contentContainerStyle={styles.listContent}
            keyboardShouldPersistTaps="handled"
          >
            {item ? (
              <View style={styles.postBlock}>
                {getBannerDetailImage(item) ? (
                  <TouchableOpacity style={[styles.postImage, {aspectRatio: aspectRatio}]} activeOpacity={0.9} onPress={handleImagePress}>
                    <Image
                      source={{ uri: getBannerDetailImage(item) }}
                      style={[styles.postImage, {aspectRatio: aspectRatio}]}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                ) : null}
                {item.description ? (
                  <Text style={styles.postCaption}>
                    {item.description}
                  </Text>
                ) : null}
              </View>
            ) : null}
            {(comments || []).length === 0 ? (
              <Text style={styles.empty}>Belum ada komentar</Text>
            ) : (
              (comments || []).map((comment) => (
                <View 
                  key={comment.id}
                  onLayout={(event) => {
                    const { y } = event.nativeEvent.layout;
                    setCommentCoordinates((prev) => ({ ...prev, [comment.id]: y }));
                  }}
                >
                  <CommentRow 
                    comment={comment} 
                    now={currentTs} 
                    currentUser={currentUser} 
                    onReply={handleReply} 
                    onDelete={handleDelete}
                    onLike={handleLike}
                  />
                  {(comment.replies || []).map((reply) => (
                    <CommentRow 
                      key={reply.id} 
                      comment={reply} 
                      now={currentTs} 
                      currentUser={currentUser} 
                      isReply 
                      onReply={handleReply} 
                      onDelete={handleDelete}
                      onLike={handleLike}
                    />
                  ))}
                </View>
              ))
            )}
          </ScrollView>

          {replyTo ? (
            <View style={styles.replyBar}>
              <Text style={styles.replyBarText}>Membalas {replyTo.user}</Text>
              <TouchableOpacity onPress={() => setReplyTo(null)} hitSlop={8}>
                <Ionicons name="close" size={16} color="#64748B" />
              </TouchableOpacity>
            </View>
          ) : null}

          <View style={styles.composer}>
            <Avatar name={currentUser?.name || 'You'} uri={currentUser?.avatar} />
            <TextInput
              ref={inputRef}
              style={styles.input}
              value={text}
              onChangeText={setText}
              placeholder={placeholder}
              placeholderTextColor="#94A3B8"
              returnKeyType="send"
              onSubmitEditing={handleSend}
            />
            <TouchableOpacity
              onPress={handleSend}
              disabled={!text.trim()}
              style={styles.sendBtn}
            >
              <Text style={[styles.sendText, !text.trim() && styles.sendDisabled]}>Kirim</Text>
            </TouchableOpacity>
          </View>
          </View>
        </View>

        {customAlertModal}
      </KeyboardAvoidingView>
    </Modal>
  );
}

const styles = StyleSheet.create({
  viewport: {
    flex: 1,
    alignItems: 'center',
  },
  outside: {
    ...StyleSheet.absoluteFillObject,
  },
  column: {
    width: '100%',
    maxWidth: 430,
    height: '100%',
    justifyContent: 'flex-end',
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0,0,0,0.45)',
  },
  sheet: {
    width: '100%',
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: 16,
    borderTopRightRadius: 16,
    maxHeight: '88%',
    minHeight: 420,
    paddingBottom: Platform.OS === 'ios' ? 24 : 12,
    zIndex: 2,
  },
  handle: {
    alignSelf: 'center',
    width: 36,
    height: 4,
    borderRadius: 2,
    backgroundColor: '#E2E8F0',
    marginTop: 8,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#E2E8F0',
  },
  headerTitle: {
    fontSize: 16,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#0F172A',
  },
  closeBtn: {
    position: 'absolute',
    right: 16,
  },
  list: {
    flexGrow: 1,
  },
  listContent: {
    paddingHorizontal: 16,
    paddingVertical: 12,
  },
  postBlock: {
    marginBottom: 16,
  },
  postImage: {
    width: '100%',
    borderRadius: 8,
    backgroundColor: '#E2E8F0',
    marginBottom: 10,
  },
  postCaption: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: '#0F172A',
    lineHeight: 18,
    marginBottom: 8,
  },
  empty: {
    textAlign: 'center',
    color: '#94A3B8',
    fontFamily: 'Poppins',
    fontSize: 13,
    paddingVertical: 32,
  },
  commentRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 10,
    marginBottom: 12,
  },
  replyRow: {
    marginLeft: 36,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#FFFFFF',
    fontSize: 12,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
  },
  commentBody: {
    flex: 1,
  },
  commentText: {
    fontSize: 13,
    fontFamily: 'Poppins',
    color: '#0F172A',
    lineHeight: 18,
    display: 'inline-block',
  },
  commentUser: {
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
  },
  commentMention: {
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    fontStyle: 'italic'
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    marginTop: 4,
  },
  meta: {
    fontSize: 11,
    color: '#94A3B8',
    fontFamily: 'Poppins',
  },
  replyLink: {
    fontSize: 11,
    color: '#64748B',
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
  },
  replyBar: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 8,
    backgroundColor: '#F8FAFC',
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E2E8F0',
  },
  replyBarText: {
    fontSize: 12,
    color: '#64748B',
    fontFamily: 'Poppins',
  },
  composer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingHorizontal: 16,
    paddingTop: 10,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: '#E2E8F0',
  },
  input: {
    flex: 1,
    fontSize: 14,
    fontFamily: 'Poppins',
    color: '#0F172A',
    paddingVertical: 8,
  },
  sendBtn: {
    paddingVertical: 6,
    paddingHorizontal: 4,
  },
  sendText: {
    fontSize: 14,
    fontFamily: 'Poppins-SemiBold',
    fontWeight: '600',
    color: '#0EA5E9',
  },
  sendDisabled: {
    color: '#BAE6FD',
  },
  delete: {
    marginTop: 4,
    marginRight: 4,
  },
  commentLike: {
    marginTop: 4,
  },
  commentLikeText: {
    fontSize: 10,
    fontFamily: 'Poppins-Medium',
    fontWeight: '500',
    color: '#0F172A',
    lineHeight: 14,
  }
});
